import React, { useMemo, useState } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import SwitchInput from '@/components/Inputs/Switch';
import Tooltip from '@/components/Tooltip';
import { getMemoryThemeColors } from './memoryTheme';

const isWeb = Platform.OS === 'web';

function InfoTip({ label, colors, styles, expanded, onPress }) {
  return (
    <Tooltip label={label} side="top" align="start" showIsXs delayDuration={150} disabled={expanded}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={{ expanded }}
        {...(isWeb ? { 'aria-expanded': expanded } : {})}
        onPress={onPress}
        hitSlop={8}
        style={({ hovered }) => [
          styles.infoButton,
          hovered && styles.infoButtonHovered,
        ]}
      >
        <Icon name="circle-info" size={14} color={colors.text_color_secondary} />
      </Pressable>
    </Tooltip>
  );
}

function MemoryPreferencesCard({
  isMemoryEnabled,
  isSaving = false,
  onToggleMemoryEnabled,
  isLearningEnabled,
  onToggleLearning,
  isPlus,
  onPlusUpsell,
}) {
  const { colors, theme } = useTheme();
  const { isXS, isSM } = useResponsiveness();
  const { t } = useTranslation();
  const [expandedInfo, setExpandedInfo] = useState(null);
  const memColors = useMemo(() => getMemoryThemeColors(theme, colors), [theme, colors]);
  const styles = useMemo(
    () => createStyles(colors, memColors, isXS || isSM),
    [colors, memColors, isXS, isSM]
  );

  const useDesc = t('pages.memory.useInConversationsDesc');
  const learnDesc = isPlus
    ? t('pages.memory.learnFromConversationsDesc')
    : t('pages.memory.learnFromConversationsDescFree');
  // Background learning is Plus-only. Stored prefs default to on, but free
  // accounts never get that job — keep the switch off and send taps to upsell.
  const learningSwitchOn = Boolean(isPlus && isLearningEnabled);

  return (
    <View style={styles.card}>
      <Text
        tag="h2"
        size="s"
        weight="semibold"
        color="secondary"
        style={styles.heading}
      >
        {t('pages.memory.preferencesTitle')}
      </Text>

      <View style={styles.togglesGrid}>
        <View style={styles.controlBox}>
          <View style={styles.controlInfo}>
            <View style={styles.controlTitleRow}>
              <Text size="s" weight="semibold" color="primary">
                {t('pages.memory.useInConversations')}
              </Text>
              <InfoTip label={useDesc} colors={colors} styles={styles} expanded={expandedInfo === 'use'} onPress={() => setExpandedInfo(value => value === 'use' ? null : 'use')} />
            </View>
            {expandedInfo === 'use' && <Text size="xs" color="secondary" style={styles.controlDesc}>
              {useDesc}
            </Text>}
          </View>
          <View style={styles.switchCol}>
            <SwitchInput
              disabled={isSaving}
              accessibilityLabel={t('pages.memory.useInConversations')}
              value={isMemoryEnabled}
              onValueChange={onToggleMemoryEnabled}
            />
          </View>
        </View>

        <View style={[styles.controlBox, styles.learningBox]}>
          <View style={styles.controlInfo}>
            <View style={styles.controlTitleRow}>
              <Text size="s" weight="semibold" color="primary">
                {t('pages.memory.learnFromConversations')}
              </Text>
              {isPlus ? (
                <View style={styles.plusBadge}>
                  <Text size="xxs" weight="bold" style={styles.plusBadgeText}>
                    PLUS
                  </Text>
                </View>
              ) : null}
              <InfoTip label={learnDesc} colors={colors} styles={styles} expanded={expandedInfo === 'learn'} onPress={() => setExpandedInfo(value => value === 'learn' ? null : 'learn')} />
            </View>
            {expandedInfo === 'learn' && <Text size="xs" color="secondary" style={styles.controlDesc}>
              {learnDesc}
            </Text>}
            {!isPlus && (
              <Pressable accessibilityRole="button" onPress={onPlusUpsell} style={styles.upsellPressable}>
                <Icon name="sparkles" size={12} color={memColors.accent} />
                <Text size="xs" weight="semibold" style={{ color: memColors.accent, flexShrink: 1 }}>
                  {t('pages.memory.learnPlusUpsell')}
                </Text>
              </Pressable>
            )}
          </View>
          <View style={styles.switchCol}>
            <SwitchInput
              disabled={isSaving}
              accessibilityLabel={t('pages.memory.learnFromConversations')}
              value={learningSwitchOn}
              onValueChange={(value) => {
                if (!isPlus) {
                  onPlusUpsell?.();
                  return;
                }
                onToggleLearning(value);
              }}
            />
          </View>
        </View>
      </View>
    </View>
  );
}

const createStyles = (colors, palette, compact) =>
  StyleSheet.create({
    card: {
      marginBottom: 20,
      gap: 8,
    },
    heading: {
      paddingHorizontal: 2,
    },
    togglesGrid: {
      flexDirection: compact ? 'column' : 'row',
      backgroundColor: palette.surface,
      borderWidth: 1,
      borderColor: palette.border,
      borderRadius: 14,
    },
    controlBox: {
      flex: compact ? undefined : 1,
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 12,
      padding: 14,
    },
    learningBox: {
      borderTopWidth: compact ? 1 : 0,
      borderLeftWidth: compact ? 0 : 1,
      borderColor: palette.border,
    },
    controlInfo: {
      flex: 1,
      minWidth: 0,
      gap: 4,
    },
    controlTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexWrap: 'wrap',
    },
    infoButton: {
      width: 28,
      height: 28,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 8,
      ...(isWeb ? { cursor: 'help' } : {}),
    },
    infoButtonHovered: {
      backgroundColor: palette.hover,
    },
    controlDesc: {
      lineHeight: 18,
    },
    switchCol: {
      flexShrink: 0,
      paddingTop: 2,
    },
    plusBadge: {
      backgroundColor: palette.preview,
      paddingHorizontal: 6,
      paddingVertical: 1,
      borderRadius: 6,
    },
    plusBadgeText: {
      color: colors.text_color_secondary,
      fontSize: 10,
    },
    upsellPressable: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      marginTop: 4,
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
  });

export default React.memo(MemoryPreferencesCard);
